export function getCategoryPaginationPage(value: string | null | undefined) {
  if (!value || !/^[1-9]\d*$/.test(value)) return undefined;
  const page = Number(value);
  return Number.isSafeInteger(page) ? page : undefined;
}

export function getCategoryPaginationLinkPage(href?: string | null) {
  if (!href) return undefined;
  try {
    const url = new URL(href, 'https://pagination.invalid');
    if (url.searchParams.getAll('page').length !== 1) return undefined;
    return getCategoryPaginationPage(url.searchParams.get('page'));
  } catch {
    return undefined;
  }
}

export function buildCategoryPaginationHref(pathname: string, search: string, page: number) {
  const query = new URLSearchParams(search);
  query.set('page', String(page));
  return `${pathname}?${query.toString()}`;
}

type PaginationInteraction = {
  button: number;
  defaultPrevented: boolean;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  target?: string;
  download?: boolean;
};

function withoutPage(query: URLSearchParams) {
  const copy = new URLSearchParams(query);
  copy.delete('page');
  copy.sort();
  return copy.toString();
}

export function getCategoryPaginationFilterIdentity(search: string) {
  return withoutPage(new URLSearchParams(search));
}

/** Null means native anchor navigation, including modified/new-tab clicks. */
export function getCategoryPaginationNavigation(
  currentHref: string,
  targetHref: string,
  event: PaginationInteraction,
): { href: string; search: string; changed: boolean } | null {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey ||
    event.download ||
    (event.target && event.target !== '_self')
  )
    return null;

  try {
    const current = new URL(currentHref);
    const target = new URL(targetHref, current);
    const page = getCategoryPaginationLinkPage(target.href);
    if (
      !page ||
      current.origin !== target.origin ||
      current.pathname !== target.pathname ||
      current.hash !== target.hash ||
      withoutPage(current.searchParams) !== withoutPage(target.searchParams)
    )
      return null;

    // Preserve the live URL's filters, their ordering and any tracking parameters.
    const href = buildCategoryPaginationHref(current.pathname, current.search, page) + current.hash;
    const next = new URL(href, current);
    const currentPage = getCategoryPaginationPage(current.searchParams.get('page')) ?? 1;
    return { href, search: next.searchParams.toString(), changed: currentPage !== page };
  } catch {
    return null;
  }
}
